import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http'
import { inject, Injectable, signal } from '@angular/core'
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router'
import { catchError, map, throwError } from 'rxjs'
import { environment } from '../../../environments/environment'
import { CACHING_ENABLED } from '../config/http.config'
import { LoginResponse } from '../interfaces'
import { ApiService } from './api.service'
import { TokenService } from './token.service'
import { BASE_URL } from "../config";

@Injectable({
    providedIn: 'root',
})
export class AuthService {

    private readonly ORBIT_URL = environment.ORBIT_URL;

    private readonly apiService = inject(ApiService)
    private readonly http = inject(HttpClient)
    private readonly router = inject(Router)
    private readonly tokenService = inject(TokenService)

    private inProgress = false

    readonly $$isLogged = signal(false)

    constructor() {
        this.syncLoggedState()
    }

    private syncLoggedState() {
        const token = this.tokenService.$$refreshToken();
        this.$$isLogged.set(!!token && token.length > 0)
        this.inProgress = false
    }

    refreshToken() {
        return this.http
            .post<any>(`${BASE_URL}/auth/refresh`, {
                refresh_token: this.tokenService.$$refreshToken()
            }, {
                context: new HttpContext().set(CACHING_ENABLED, false)
            })
            .pipe(
                map((res) => {
                    // Expect { session_token, refresh_token (optional) }
                    if (res.session_token) {
                        this.tokenService.save({
                            accessToken: res.session_token,
                            refreshToken: res.refresh_token || this.tokenService.$$refreshToken()!
                        });
                        this.syncLoggedState();
                    }
                    return res
                })
            )
    }

    signOut(url?: string) {
        // Call backend to invalidate access
        this.http.post(`${BASE_URL}/auth/logout`, {
            refresh_token: this.tokenService.$$refreshToken()
        }).subscribe();

        this.tokenService.clear()
        this.syncLoggedState()

        // Redirect to Orbit Logout
        const redirect = url || window.location.href;
        window.location.href = `${this.ORBIT_URL}/api/auth/logout?app_code=nike_tracker&redirect=${encodeURIComponent(redirect)}`;
    }

    redirectToLogin(url?: string) {
        // Clear any stale tokens and send the user to Orbit login to re-authenticate.
        // NOTE: do NOT hit the logout endpoint here — that destroys the Orbit SSO session.
        this.tokenService.clear()
        this.syncLoggedState()

        const redirect = url || window.location.href;
        window.location.href = `${this.ORBIT_URL}/login?app_code=nike_tracker&redirect=${encodeURIComponent(redirect)}`;
    }

    ignoreLoginCheck() {
        const url = new URL(window.location.href)
        const params = new URLSearchParams(url.search)
        return params.has('ilc');
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        const sessionToken = route.queryParams['session_token'];
        const refreshToken = route.queryParams['refresh_token'];

        if (sessionToken) {
            const finalRefreshToken = refreshToken || 'nike_session';

            this.tokenService.save({
                accessToken: sessionToken,
                refreshToken: finalRefreshToken
            });
            this.syncLoggedState();

            const urlTree = this.router.parseUrl(state.url);
            delete urlTree.queryParams['session_token'];
            delete urlTree.queryParams['refresh_token'];
            delete urlTree.queryParams['app_code'];

            return urlTree;
        }

        if (!this.$$isLogged()) {
            const currentUrl = window.location.href;

            if (route.queryParams['session_token']) return true;

            // Auto-navigation disabled: don't redirect to Orbit login on open —
            // just let the app load.
            // window.location.href = `${this.ORBIT_URL}/login?app_code=nike_tracker&redirect=${encodeURIComponent(currentUrl)}`;
            // return false;
            return true;
        }
        return true;
    }

    // Deprecated / Custom Login (kept for compatibility)
    signInWith(authKey: string) {
        // ... implementation kept in case needed, but main flow is redirect
        const headers = new HttpHeaders().set('auth-key', authKey);
        return this.http.post<LoginResponse>(`${BASE_URL}/auth/login`, {}, { headers })
    }

    public homePage(): void {
        this.router.navigate(['/home']);
    }


}
